import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RidgeTexture } from '../ui/RidgeTexture';
import { LoadingFacts } from '../ui/LoadingFacts';
import { Button } from '../ui/Button';
import type { GenerationStatus } from '@shared/types/learning-path';

interface LearningGenerationScreenProps {
  generationStatus: GenerationStatus | null;
  generationProgress: { totalNodes: number; completedNodes: number } | null;
  onReturnToDashboard: () => void;
  onStop?: () => void;
  onDelete?: () => void;
}

const stages = [
  {
    key: null,
    label: 'Connecting to CogniPrint...',
    detail: 'We\'re about to do something no textbook, summary tool, or generic AI can do — rebuild this material around how YOUR brain actually works.',
    estimate: '',
  },
  {
    key: 'generating_path',
    label: 'Designing your learning architecture...',
    detail: 'Right now, our most powerful AI is reading your CogniPrint profile and your content side by side. It\'s deciding the order you\'ll see concepts, what format each section should use, where to place checkpoints, and how deep to go — all based on how you learn. Two people with different profiles uploading this same document would get completely different paths. This one is yours.',
    estimate: 'Usually takes 1–2 minutes',
  },
  {
    key: 'generating_content',
    label: 'Writing your personalised content...',
    detail: 'Every section is being written from scratch — not copied from your document, but explained in a way that matches your learning style. Visual learner? You\'re getting diagrams and spatial explanations. Theory-first thinker? Principles before examples. Need depth? You\'re getting the full picture, not a summary. This is the part that makes CogniPrint different from everything else.',
    estimate: 'Usually takes 2–4 minutes',
  },
  {
    key: 'curating_videos',
    label: 'Finding relevant videos...',
    detail: 'Searching for short, high-quality video explanations that complement your content. If we can\'t find anything good enough, we\'ll create a visual explanation instead.',
    estimate: 'Usually takes 15–30 seconds',
  },
  {
    key: 'complete',
    label: 'Your learning path is ready!',
    detail: 'What you\'re about to see has never existed before — a learning experience built for exactly one person. You.',
    estimate: '',
  },
] as const;

function getStageIndex(status: GenerationStatus | null): number {
  const idx = stages.findIndex((s) => s.key === status);
  return idx >= 0 ? idx : 0;
}

export function LearningGenerationScreen({
  generationStatus,
  generationProgress,
  onReturnToDashboard,
  onStop,
  onDelete,
}: LearningGenerationScreenProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [stageStartTime, setStageStartTime] = useState(Date.now());

  const stageIndex = getStageIndex(generationStatus);
  const stage = stages[stageIndex];
  const isError = generationStatus === 'error';
  const isProcessing = !isError && generationStatus !== 'complete';

  // stageWeights[i] is the END of stage i. Stage i runs [prev end, stageWeights[i]].
  // generating_content is the long one and gets its own per-node sub-progress.
  const stageWeights = [5, 30, 90, 95, 100];
  const stageStart = stageIndex === 0 ? 0 : stageWeights[stageIndex - 1];
  const stageEnd = stageWeights[stageIndex];
  const subProgress = generationStatus === 'generating_content' && generationProgress
    ? (generationProgress.completedNodes / Math.max(generationProgress.totalNodes, 1)) * (stageEnd - stageStart)
    : 0;
  const overallProgress = generationStatus === 'complete'
    ? 100
    : Math.min(stageStart + subProgress, 99);

  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [isProcessing]);

  useEffect(() => {
    setStageStartTime(Date.now());
  }, [generationStatus]);

  void stageStartTime;

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <div className="min-h-screen bg-dark-bg relative flex items-center justify-center">
      <RidgeTexture opacity="opacity-[0.035]" />

      <div className="relative z-10 max-w-xl mx-auto px-4 text-center">
        {/* Professor Cogni mascot */}
        <motion.img
          src="/WavingCog.png" alt="Professor Cogni"
          className="w-24 h-24 mx-auto mb-6 object-contain"
          animate={isProcessing ? { y: [0, -4, 0] } : { scale: [1, 1.05, 1] }}
          transition={isProcessing ? { duration: 2, repeat: Infinity, ease: 'easeInOut' } : { duration: 1.5, repeat: Infinity }}
        />

        {/* Progress bar */}
        {!isError && (
          <div className="max-w-md mx-auto mb-6">
            <div className="h-2 rounded-pill bg-dark-subtle overflow-hidden">
              <motion.div
                className="h-full rounded-pill bg-gradient-to-r from-indigo-500 to-indigo-400"
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-content font-body">{Math.round(overallProgress)}%</span>
              {isProcessing && (
                <span className="text-sm text-content-secondary font-body">{formatTime(elapsedSeconds)} elapsed</span>
              )}
            </div>
          </div>
        )}

        {/* Stage info */}
        {!isError && (
          <div className="mb-4">
            <p className="text-lg text-content font-body font-semibold mb-2">{stage.label}</p>
            {stage.detail && (
              <p className="text-sm text-content-secondary font-body mt-2 max-w-md mx-auto leading-relaxed">{stage.detail}</p>
            )}
          </div>
        )}

        {/* Content generation progress detail */}
        {generationStatus === 'generating_content' && generationProgress && (
          <div className="mt-3 mb-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill bg-dark-surface border border-dark-border">
              <motion.div
                className="w-2 h-2 rounded-full bg-indigo-500"
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <span className="text-sm text-content font-body font-medium">
                {generationProgress.completedNodes} of {generationProgress.totalNodes} sections complete
              </span>
            </div>
          </div>
        )}

        {/* Time estimate */}
        {isProcessing && stage.estimate && (
          <p className="text-sm text-content-secondary font-body mt-3 mb-4">{stage.estimate}</p>
        )}

        {/* Spinner */}
        {isProcessing && (
          <div className="flex justify-center my-5">
            <motion.div
              className="w-8 h-8 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
          </div>
        )}

        {/* Stage steps indicator */}
        {!isError && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {['Path', 'Content', 'Videos'].map((step, i) => {
              const stepIndex = i + 1;
              const isActive = stageIndex === stepIndex;
              const isDone = stageIndex > stepIndex;
              return (
                <div key={step} className="flex items-center gap-2">
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-pill text-xs font-body font-semibold
                    ${isDone ? 'bg-success/15 text-success' :
                      isActive ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30' :
                      'bg-dark-surface text-content-muted border border-dark-border'}`}
                  >
                    {isDone ? '✓' : isActive ? '●' : '○'} {step}
                  </div>
                  {i < 2 && <div className={`w-4 h-px ${isDone ? 'bg-success/30' : 'bg-dark-border'}`} />}
                </div>
              );
            })}
          </div>
        )}

        {/* Reassurance + what you'll get */}
        {isProcessing && elapsedSeconds > 20 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg bg-dark-surface border border-dark-border p-5 mb-4 text-left max-w-md mx-auto"
          >
            <p className="text-sm text-gold-500 font-body font-semibold uppercase tracking-wider mb-3">What you're about to get</p>
            <ul className="space-y-2 text-sm text-content font-body">
              <li>✦ Content restructured around how you process information</li>
              <li>✦ Concepts sequenced for your preferred learning approach</li>
              <li>✦ Depth calibrated to your tolerance — no fluff, no gaps</li>
              <li>✦ Comprehension checkpoints matched to your profile</li>
              <li>✦ An AI Tutor that knows your content and your CogniPrint</li>
            </ul>
          </motion.div>
        )}
        {isProcessing && elapsedSeconds > 60 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-content-secondary font-body mb-4"
          >
            This is running on our servers — feel free to close this tab and come back. Your learning path will be waiting.
          </motion.p>
        )}

        {/* Stop / Delete controls during generation */}
        {isProcessing && (onStop || onDelete) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 5 }}
            className="flex items-center justify-center gap-3 mb-4"
          >
            {onStop && (
              <button
                onClick={onStop}
                className="text-xs text-content-muted hover:text-warning font-body transition-colors cursor-pointer"
              >
                Stop Generation
              </button>
            )}
            {onStop && onDelete && (
              <span className="text-content-dim text-xs">|</span>
            )}
            {onDelete && !confirmDelete && (
              <button
                onClick={() => setConfirmDelete(true)}
                className="text-xs text-content-muted hover:text-error font-body transition-colors cursor-pointer"
              >
                Delete Path
              </button>
            )}
            {onDelete && confirmDelete && (
              <span className="flex items-center gap-2">
                <span className="text-xs text-error font-body">Delete this path?</span>
                <button
                  onClick={onDelete}
                  className="text-xs text-error font-body font-semibold hover:underline cursor-pointer"
                >
                  Yes, delete
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-xs text-content-muted font-body hover:text-content cursor-pointer"
                >
                  Cancel
                </button>
              </span>
            )}
          </motion.div>
        )}

        {/* Error */}
        {isError && (
          <div>
            <p className="text-error font-body text-lg mb-2">Something went wrong generating your learning path.</p>
            <p className="text-sm text-content-secondary font-body mb-6">This can happen with very large or complex documents. Try again or contact support.</p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={onReturnToDashboard}>Return to Dashboard</Button>
              {onDelete && (
                <Button variant="ghost" onClick={onDelete}>
                  Delete Path
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Facts */}
        {isProcessing && <LoadingFacts />}
      </div>
    </div>
  );
}
